import { useState, useEffect, useCallback } from 'react';

import apiService from '../services/api';
import { SpecialOffer } from '../types';

// LIFF 再起動をまたいで表示済みオファーを記憶するため localStorage を使用。
// sessionStorage は LIFF 終了でクリアされるため使えない。
const SEEN_KEY = 'gami-seen-offer-ids';

const getSeenIds = (): Set<string> => {
  try {
    const raw = localStorage.getItem(SEEN_KEY);
    return new Set(raw ? (JSON.parse(raw) as string[]) : []);
  } catch {
    return new Set();
  }
};

const addSeenIds = (ids: string[]): void => {
  try {
    const seen = getSeenIds();
    ids.forEach((id) => seen.add(id));
    localStorage.setItem(SEEN_KEY, JSON.stringify(Array.from(seen)));
  } catch {
    /* localStorage 不可環境では guard 無し */
  }
};

interface UseInvitationCelebrationResult {
  pendingInvitations: SpecialOffer[];
  closeInvitation: () => void;
}

/**
 * 未確認招待状（invitation）演出フック。
 *
 * 特別オファーページロード時に全登録店舗（storeUserIds）の special-offers を確認し、
 * offer_type='invitation' かつ status='sent' かつ未表示（localStorage 未記録）の
 * ものがあれば演出を表示する（useGetSkillCelebration と同じ全店舗集約の方針）。
 *
 * 冪等性の2層構造:
 *   1. localStorage: 表示済みの offer_id を記録。LIFF 再起動後も残るため
 *      DB 更新が完了しなくても再表示を防げる（主防衛ライン）。
 *   2. DB (status='opened'): respondSpecialOffer を best-effort で呼び、
 *      offer の status を 'sent' → 'opened' に更新する（副防衛ライン）。
 *      オファー自身が持つ store_user_id を使い、発行元の店舗に対して PATCH する。
 */
export const useInvitationCelebration = (
  storeUserIds: string[],
  enabled: boolean = true,
): UseInvitationCelebrationResult => {
  const [pendingInvitations, setPendingInvitations] = useState<SpecialOffer[]>([]);

  // storeUserIds の内容が変わった時だけ effect を再実行するため文字列化して比較する。
  const storeUserIdsKey = storeUserIds.join(',');

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      if (!enabled || storeUserIds.length === 0) return;
      try {
        const results = await Promise.all(
          storeUserIds.map((suId) => apiService.getSpecialOffers(suId)),
        );
        if (cancelled) return;
        const sentInvitations = results
          .filter((res) => res.success && res.data)
          .flatMap((res) => res.data as SpecialOffer[])
          .filter((o) => o.offer_type === 'invitation' && o.status === 'sent');
        // localStorage で表示済みのものを除外（LIFF 再起動をまたいで有効）。
        const seenIds = getSeenIds();
        const freshInvitations = sentInvitations.filter((o) => !seenIds.has(o.offer_id));
        if (freshInvitations.length > 0) {
          // 先に localStorage へ記録（LIFF が即時閉じられても再表示しない）。
          addSeenIds(freshInvitations.map((o) => o.offer_id));
          setPendingInvitations(freshInvitations);
          // DB の status を 'opened' に更新（best-effort・副防衛ライン）。発行元店舗ごとに PATCH。
          freshInvitations.forEach((inv) =>
            apiService.respondSpecialOffer(inv.store_user_id, inv.offer_id, 'opened').catch(() => {}),
          );
        }
      } catch {
        /* 非クリティカル：表示しないで続行 */
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storeUserIdsKey, enabled]);

  const closeInvitation = useCallback(() => setPendingInvitations([]), []);

  return { pendingInvitations, closeInvitation };
};

export default useInvitationCelebration;
